import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import {
  BarChart,
  formatRub,
  HealthRing,
  InsightList,
  MetricCard,
  TopClubsTable,
} from "./PlatformDashboardParts";
import s from "./platformAdmin.module.css";

export default function PlatformDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    platformAPI
      .getAnalytics()
      .then((r) => setData(r.analytics))
      .catch((e) =>
        setErr(e.response?.data?.error || "Не удалось загрузить аналитику"),
      )
      .finally(() => setLoading(false));
  }, []);

  const o = data?.overview;
  const g = data?.growth;
  const a = data?.activity;
  const f = data?.forecast;
  const status = o?.tenants_by_status || {};

  return (
    <div className={s.page}>
      <header className={s.pageHero}>
        <div>
          <div className={s.eyebrow}>BaySport · платформа</div>
          <h1 className={s.pageTitle}>Сводка сервиса</h1>
          <p className={s.pageLead}>
            Активность клубов, рост базы, прогнозы и здоровье SaaS-платформы.
          </p>
        </div>
        {o ? <HealthRing score={o.health_score} /> : null}
      </header>

      {err ? <div className={s.alertError}>{err}</div> : null}
      {loading ? <div className={s.loading}>Загрузка метрик…</div> : null}

      {o ? (
        <>
          <section className={s.metricGrid}>
            <MetricCard
              label="Клубов на платформе"
              value={o.tenants_total}
              hint={`Активных: ${status.active ?? 0} · Пробных: ${status.trial ?? 0}`}
              accent
            />
            <MetricCard
              label="MRR (оценка)"
              value={`${formatRub(o.mrr_estimate_rub)} ₽`}
              hint={`ARR ~ ${formatRub(o.arr_estimate_rub)} ₽`}
            />
            <MetricCard
              label="Новых клубов · 30д"
              value={g?.new_tenants_30d ?? 0}
              hint={`7д: +${g?.new_tenants_7d ?? 0}`}
              trend={f?.tenants?.trend}
            />
            <MetricCard
              label="Активных клубов · 30д"
              value={a?.active_clubs_30d ?? 0}
              hint={`Вовлечённость ${a?.utilization_pct ?? 0}%`}
            />
            <MetricCard
              label="Занятий · 30д"
              value={a?.sessions_30d ?? 0}
              hint={`7д: ${a?.sessions_7d ?? 0}`}
              trend={f?.sessions?.trend}
            />
            <MetricCard
              label="Спортсменов"
              value={formatRub(o.clients_total)}
              hint={`+${a?.new_clients_30d ?? 0} за 30д`}
            />
            <MetricCard
              label="Тренеров"
              value={o.trainers_total}
              hint={`Сотрудников: ${o.staff_total}`}
            />
            <MetricCard
              label="Входы staff · 7д"
              value={a?.staff_logins_7d ?? 0}
              hint="По refresh-сессиям"
            />
          </section>

          <section className={s.twoCol}>
            <BarChart
              title="Регистрации клубов по неделям"
              series={g?.signups_weekly || []}
            />
            <BarChart
              title="Занятия по неделям"
              series={a?.sessions_weekly || []}
            />
          </section>

          <section className={s.twoCol}>
            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Прогноз</h3>
              <div className={s.forecastGrid}>
                <div className={s.forecastCard}>
                  <span>Новые клубы (тренд)</span>
                  <strong>~{f?.tenants?.next ?? 0} / мес</strong>
                </div>
                <div className={s.forecastCard}>
                  <span>MRR через 30 дней</span>
                  <strong>{formatRub(f?.mrr_next_30d_rub ?? 0)} ₽</strong>
                </div>
                <div className={s.forecastCard}>
                  <span>Рост регистраций</span>
                  <strong>
                    {g?.growth_rate_pct > 0 ? "+" : ""}
                    {g?.growth_rate_pct ?? 0}%
                  </strong>
                </div>
                <div className={s.forecastCard}>
                  <span>Среднее занятий / активный клуб</span>
                  <strong>{a?.avg_sessions_per_active_club ?? 0}</strong>
                </div>
              </div>
            </div>

            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Риски и внимание</h3>
              <div className={s.riskGrid}>
                <div className={s.riskItem}>
                  <span>Пробные без активности 14д+</span>
                  <strong>{data?.at_risk?.inactive_trials ?? 0}</strong>
                </div>
                <div className={s.riskItem}>
                  <span>Подписка истекает ≤14д</span>
                  <strong>{data?.at_risk?.expiring_soon ?? 0}</strong>
                </div>
                <div className={s.riskItem}>
                  <span>Отключённые</span>
                  <strong>{data?.at_risk?.suspended ?? 0}</strong>
                </div>
                <div className={s.riskItem}>
                  <span>Отметок посещаемости · 30д</span>
                  <strong>{a?.attendances_30d ?? 0}</strong>
                </div>
              </div>
              <Link to="/admin/tenants" className={s.linkBtn}>
                Управление клубами →
              </Link>
            </div>
          </section>

          <section className={s.chartPanel}>
            <h3 className={s.panelTitle}>Инсайты</h3>
            <InsightList items={data?.insights || []} />
          </section>

          <section className={s.threeCol}>
            <TopClubsTable
              title="Топ по спортсменам"
              rows={data?.top_clubs?.by_clients}
              valueKey="clients"
            />
            <TopClubsTable
              title="Топ по занятиям (30д)"
              rows={data?.top_clubs?.by_sessions}
              valueKey="sessions_30d"
            />
            <TopClubsTable
              title="Топ по активности"
              rows={data?.top_clubs?.by_activity_score}
              valueKey="score"
            />
          </section>

          {o.tenants_by_plan ? (
            <section className={s.chartPanel}>
              <h3 className={s.panelTitle}>Распределение по тарифам</h3>
              <div className={s.chips}>
                {Object.entries(o.tenants_by_plan).map(([name, count]) => (
                  <span key={name} className={s.chip}>
                    {name}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
